import { Col, Label } from 'reactstrap';

import CreatableSelect from 'react-select/creatable';

import { useTranslation } from 'react-i18next';

import { FormikProps } from 'formik';

import { useCallback, useState } from 'react';
import { RootState } from 'slices';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { CheckboxTagOption } from 'Components/pos2u/CustomReactSelectComponents/CustomReactSelectOption';

import { ProductFormDetailsInterface } from 'types/productTypes';
import { getTags } from 'services/tagService';
import { mapTagsToOptionsStr } from 'utils/mappers/tagMapper';

import { setSelectMultiTags } from 'slices/product/form/reducer';
import {
  LEAST_CREATE_NEW_TAG_LENGTH,
  LEAST_SEARCH_LENGTH,
  SELECTED_TAGS_LIMIT,
} from 'pages/Products/ProductConstants';
import { CustomMultiValueTagLabel } from 'Components/pos2u/CustomReactSelectComponents/CustomMultiValueLabel';
import { multiSelectTagStyle } from 'styles/common/multiSelectTagStyle';
import { SelectedTagData, TagOptionStr } from 'types/tag/TagTypes';

type MultiValue<T> = readonly T[];
type ActionMeta = { action: string };

const ProductTag = ({
  formik,
}: {
  formik: FormikProps<ProductFormDetailsInterface>;
}) => {
  const { t } = useTranslation();

  const dispatch: ThunkDispatch<RootState, void, Action> = useDispatch();

  const layoutState = useSelector((state: RootState) => state.Layout);
  const productState = useSelector((state: RootState) => state.Product);
  const { selectMultiTags } = productState.form;

  const { layoutModeType } = layoutState;

  const isDarkMode = layoutModeType === 'dark';

  const [inputTagValue, setInputTagValue] = useState('');
  const [tagOptions, setTagOptions] = useState<TagOptionStr[]>([]);

  // const [hasPermission, setHasPermission] = useState(true);
  const hasPermission = true;

  const [exceedLimitTags, setExceedLimitTags] = useState<boolean>(false);

  const transformOptionsToListsArray = (
    data: SelectedTagData[] | null | undefined
  ) => {
    const lists: string[] = [];
    if (data && Array.isArray(data)) {
      data.forEach(({ value }) => {
        lists.push(value);
      });
    }
    return lists;
  };

  const handleSearch = useCallback(async (term: string) => {
    if (term.length < LEAST_SEARCH_LENGTH) return;

    try {
      const results = await getTags(term, 1, -1, 1);

      if (results) {
        const { message, error_code, data } = results;

        if (message === 'OK' && error_code === 0) {
          if (data && data.Tags) {
            const searchResultsOptions = await mapTagsToOptionsStr(data.Tags);
            setTagOptions(searchResultsOptions);
          } else {
            setTagOptions([]);
          }
        } else {
          setTagOptions([]);
        }
      } else {
        console.error('No response data returned');
        setTagOptions([]);
      }
    } catch (error) {
      console.log(error);
      setTagOptions([]);
    }
  }, []);
  const handleInputProductTagsChange = (newValue: string) => {
    setInputTagValue(newValue);
    handleSearch(newValue);
  };

  const handleChangeProductTags = (
    selectedOptions: MultiValue<SelectedTagData>,
    _actionMeta: ActionMeta
  ) => {
    const selected = selectedOptions as SelectedTagData[];

    if (selected.length > SELECTED_TAGS_LIMIT) {
      setExceedLimitTags(true);
      return;
    }
    setExceedLimitTags(false);
    dispatch(setSelectMultiTags(selected));

    formik
      .setFieldValue(`tags`, transformOptionsToListsArray(selected))
      .then(() => formik.setFieldTouched(`tags`, true));
  };

  const handleCreateNewTag = (input: string) => {
    const lowerTrimmed = input.trim();
    if (lowerTrimmed.length < LEAST_CREATE_NEW_TAG_LENGTH) return;
    const newOption: SelectedTagData = {
      value: `new:${input}`,
      label: input,
      color: '#4bb189',
    };
    const newSelection = [...selectMultiTags, newOption];
    const exists = selectMultiTags.some(
      (opt) => opt.label.trim().toLowerCase() === lowerTrimmed
    );

    if (exists) return;

    dispatch(setSelectMultiTags(newSelection));

    formik
      .setFieldValue('tags', transformOptionsToListsArray(newSelection))
      .then(() => {
        formik.setFieldTouched('tags', true);
      });
  };

  return (
    <div
      className={`mb-3 row ${hasPermission ? '' : 'd-none'}`}
      data-testid="product-tag-select"
    >
      <Label htmlFor="ConditionType" className="col-sm-3 col-form-label">
        {t('Tags')}
      </Label>
      <Col sm={9}>
        <CreatableSelect
          id="product-tags"
          name="product-tags"
          components={{
            MultiValueLabel: CustomMultiValueTagLabel,
            Option: CheckboxTagOption,
          }}
          value={selectMultiTags}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          onChange={handleChangeProductTags}
          inputValue={inputTagValue}
          onInputChange={handleInputProductTagsChange}
          onCreateOption={handleCreateNewTag}
          isValidNewOption={(inputValue, _, options) => {
            const trimmed = inputValue.trim();
            const alreadyExists = options.some(
              (opt: any) =>
                opt.label.trim().toLowerCase() === trimmed.toLowerCase()
            );
            const newCreateExist = selectMultiTags.some(
              (opt) => opt.label.trim().toLowerCase() === trimmed.toLowerCase()
            );
            return (
              trimmed.length >= LEAST_CREATE_NEW_TAG_LENGTH &&
              trimmed.length <= 100 &&
              !alreadyExists &&
              !newCreateExist &&
              !options.some((opt) => opt.label === trimmed) &&
              selectMultiTags.length < SELECTED_TAGS_LIMIT
            );
          }}
          options={tagOptions}
          formatOptionLabel={(option: SelectedTagData) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {option.label}
            </div>
          )}
          formatCreateLabel={(inputValue) => `${t('Create')} "${inputValue}"`}
          styles={multiSelectTagStyle(isDarkMode)}
          placeholder={t('ProductPage_PleaseSelect')}
        />
        <div className="mt-1 text-danger" style={{ minHeight: '20px' }}>
          {exceedLimitTags && (
            <span>
              {t('Cannot select more than') +
                ` ${SELECTED_TAGS_LIMIT} ` +
                t('tags')}
            </span>
          )}
        </div>
      </Col>
    </div>
  );
};
export default ProductTag;
